import {
    denormalizeHighlights,
    normalizeFetchAnswerCategoryHistoryResponse,
    normalizeFetchAnswerCategoryMismatchPredictionsResponse,
    normalizeFetchEscalatedAnswersResponse,
    normalizeFetchExplanationResponse,
    normalizeFetchQuestionAnswersResponse,
    normalizeFetchSimilarAnswersResponse,
    normalizeFetchSimilarSubmissionsBySolutionResponse,
    normalizeFetchSubmissionReviewsResponse,
    normalizeFetchSubmissionsSummaryResponse,
    normalizeFetchSubmissionVersionsResponse,
    normalizeFetchTextAssignmentSubmissionsResponse,
    normalizeFetchTextSubmissionDetailsResponse,
    normalizeReviewSubmissionResponse,
    normalizeFetchSubmissionAttachmentsResponse,
} from './utils/normalizers/submission.normalizers'
import { SUBMISSIONS_SUMMARY_PAGE_SIZE } from '../../pages/resources/assignments/components/modals/submissions-summary/SubmissionsSummaryModal'
import {
    ANSWER_CATEGORY_TAG,
    APPROVE_ANALYTICS_TAG,
    ATTACHMENT_TAG,
    COMPONENT_TAG,
    createTagsForList,
    CRITERIA_FILTER_TAG,
    ESCALATED_ANSWER_TAG,
    GROUP_TAG,
    PATTERN_TAG,
    SUBMISSION_TAG,
} from './utils/tags'
import {
    onReviewQuestionAnswerQueryStarted,
    onReviewSubmissionQueryStarted,
    onUpdateQuestionAnswerEscalatedQueryStarted,
    onUpdateQuestionAnswerNoteQueryStarted,
    onUpdateQuestionAnswerQueryStarted,
    onUpdateQuestionAnswerReasoningQueryStarted,
    onUpdateTextSubmissionQueryStarted,
} from './utils/response-handlers/submission.api.response-handlers'
import baseApi from './base.api'
import { createSearchParams } from '../../utils/request.helper'

export const buildFetchSubmissionsDetailsUrl = ({ token }) => `/assignment/${token}/get-files`

export const submissionApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        fetchSubmissionsDetails: builder.query({
            query: ({ token, versionId, layerId, includeAnon }) => ({
                url: buildFetchSubmissionsDetailsUrl({ token }),
                method: 'GET',
                params: {
                    version_id: versionId,
                    layer_id: layerId,
                    include_anon: includeAnon,
                },
                //enablePersistCache: true, // disabled for now, causing too many issues
            }),
            //keepUnusedDataFor: 0, // Disable RTK Query's caching
            //refetchOnMountOrArgChange: true, // Always refetch when the component mounts
        }),
        fetchSimilarSubmissionsBySolution: builder.query({
            query: ({ token, layerId, solution, patternId }) => ({
                url: `/nl-feedback/${token}/${layerId}/${patternId}/similar-submissions`,
                method: 'POST',
                body: { content: solution },
            }),
            transformResponse: normalizeFetchSimilarSubmissionsBySolutionResponse,
        }),
        fetchSubmissionVersions: builder.query({
            query: ({ token, layerId, submissionId }) => ({
                url: `/assignment-file/${token}/${layerId}/${submissionId}/versions`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchSubmissionVersionsResponse,
        }),
        generateSimilarSubmissions: builder.mutation({
            query: ({ token, layerId, patternId, submissionId, numberOfSubmissionsToGenerate }) => ({
                url: `/assignments-upload/${token}/add-similar-synthetic-submissions`,
                method: 'POST',
                body: {
                    layer_id: layerId,
                    pattern_id: patternId,
                    submission_id: submissionId,
                    number_of_results: numberOfSubmissionsToGenerate,
                },
            }),
        }),
        fetchSubmissionsSummary: builder.query({
            query: ({ token, page = 1 }) => ({
                url: `/assignment/${token}/get-all-files-feedback`,
                method: 'POST',
                params: {
                    page,
                    page_size: SUBMISSIONS_SUMMARY_PAGE_SIZE,
                },
                body: {
                    with_content: false,
                    export: null,
                },
            }),
            transformResponse: normalizeFetchSubmissionsSummaryResponse,
            providesTags: [
                { type: GROUP_TAG, id: 'LIST' },
                { type: COMPONENT_TAG, id: 'LIST' },
                { type: PATTERN_TAG, id: 'LIST' },
            ],
        }),
        downloadSubmissionsSummary: builder.query({
            query: ({ token, type }) => ({
                url: `/assignment/${token}/get-all-files-feedback`,
                method: 'POST',
                body: {
                    with_content: true,
                    export: type,
                },
                responseType: 'blob', // handle blob response type
            }),
        }),
        fetchSubmissionReviews: builder.query({
            query: ({ token, layerId }) => ({
                url: `/assignment-file/${token}/${layerId}/reviewed`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchSubmissionReviewsResponse,
        }),
        reviewSubmission: builder.mutation({
            query: ({
                token,
                layerId,
                submissionId,
                newPatternId,
                newReasoning,
                removePattern,
                isPatternCorrected,
                isReasoningCorrected,
            }) => ({
                url: `/assignment-file/${token}/${layerId}/${submissionId}/review`,
                method: 'POST',
                body: {
                    new_pattern_id: isPatternCorrected ? newPatternId : null,
                    new_reasoning: isPatternCorrected || isReasoningCorrected ? newReasoning : null,
                    nullify_pattern: removePattern,
                },
            }),
            transformResponse: normalizeReviewSubmissionResponse,
            onQueryStarted: onReviewSubmissionQueryStarted,
        }),
        fetchNotConfidentSubmissions: builder.query({
            query: ({ token, layerId }) => ({
                url: `/assignment-file/${token}/${layerId}/not-confident`,
                method: 'GET',
            }),
        }),
        fetchTextAssignmentSubmissions: builder.query({
            query: ({ token, testSetOnly = false }) => ({
                url: `/version-students/${token}/by-version`,
                method: 'GET',
                params: {
                    in_test_set: testSetOnly,
                },
            }),
            transformResponse: normalizeFetchTextAssignmentSubmissionsResponse,
            providesTags: result => createTagsForList(result, SUBMISSION_TAG),
        }),
        fetchQuestionAnswers: builder.query({
            query: ({ id, search, assigneeIds, testSetOnly = false }) => ({
                url: `/submissions/${id}/by-question`,
                method: 'GET',
                params: createSearchParams({
                    submission_name_or_text: search,
                    reviewer_ids: assigneeIds,
                    in_test_set: testSetOnly,
                }),
            }),
            transformResponse: normalizeFetchQuestionAnswersResponse,
            providesTags: result => createTagsForList(result, SUBMISSION_TAG),
        }),
        fetchTextSubmissionDetails: builder.query({
            query: ({ submissionId }) => ({
                url: `/version-students/${submissionId}/by-student`,
                method: 'GET',
            }),
            transformResponse: (response, meta, arg) =>
                normalizeFetchTextSubmissionDetailsResponse(response, arg.submissionId),
        }),
        reviewQuestionAnswer: builder.mutation({
            query: ({
                id,
                categoryId,
                mismatchId,
                reasoning,
                explainabilityReasoning,
                explainabilityStatus,
                isAdequate,
                highlights,
                ethicalCheckOutcome,
                approvalSpeedMs,
            }) => ({
                url: `/submission-pattern/${id}/${categoryId}/review`,
                method: 'POST',
                body: {
                    reasoning,
                    explainability_reasoning: explainabilityReasoning,
                    explainability_status: explainabilityStatus,
                    is_classification_adequate: isAdequate,
                    mismatch_id: mismatchId,
                    ...(highlights ? { highlights: denormalizeHighlights(highlights) } : {}),
                    ethical_check_outcome: ethicalCheckOutcome,
                    approve_speed_ms: approvalSpeedMs
                },
            }),
            onQueryStarted: onReviewQuestionAnswerQueryStarted,
            invalidatesTags: (result, error, arg) => [
                {
                    type: ANSWER_CATEGORY_TAG,
                    id: arg.id,
                },
                { type: ANSWER_CATEGORY_TAG, id: 'LIST' },
                APPROVE_ANALYTICS_TAG,
            ],
        }),
        fetchExplanation: builder.query({
            query: ({ answerId, categoryId }) => ({
                url: `/submission-pattern/${answerId}/${categoryId}/explain`,
                method: 'GET',
            }),
            transformResponse: (response, meta, arg) => normalizeFetchExplanationResponse(response, arg),
        }),
        approveAllQuestionAnswersByCategory: builder.mutation({
            query: ({ categoryId }) => ({
                url: `/submission-pattern/${categoryId}/approve-by-pattern`,
                method: 'POST',
            }),
            invalidatesTags: [SUBMISSION_TAG],
        }),
        updateQuestionAnswer: builder.mutation({
            query: ({ id, feedback }) => ({
                url: `/submissions/${id}`,
                method: 'PATCH',
                body: { feedback },
            }),
            onQueryStarted: onUpdateQuestionAnswerQueryStarted,
        }),
        updateQuestionAnswerReasoning: builder.mutation({
            query: ({ id, reasoning }) => ({
                url: `/submissions/${id}/reasoning`,
                method: 'PATCH',
                body: { reasoning },
            }),
            onQueryStarted: onUpdateQuestionAnswerReasoningQueryStarted,
        }),
        updateQuestionAnswerNote: builder.mutation({
            query: ({ id, note }) => ({
                url: `/submissions/${id}`,
                method: 'PATCH',
                body: { internal_note: note },
            }),
            onQueryStarted: onUpdateQuestionAnswerNoteQueryStarted,
        }),
        updateQuestionAnswerEscalated: builder.mutation({
            query: ({ id, escalated, note }) => ({
                url: `/submissions/${id}/update-escalated`,
                method: 'PATCH',
                params: {
                    escalated,
                    note,
                },
            }),
            onQueryStarted: onUpdateQuestionAnswerEscalatedQueryStarted,
            invalidatesTags: [ESCALATED_ANSWER_TAG],
        }),
        fetchEscalatedQuestionAnswers: builder.query({
            query: ({ token, versionId }) => ({
                url: `/submissions/${token}/escalated`,
                method: 'GET',
                params: { version_id: versionId },
            }),
            providesTags: [ESCALATED_ANSWER_TAG],
            transformResponse: normalizeFetchEscalatedAnswersResponse,
        }),
        updateTextSubmission: builder.mutation({
            query: ({ id, feedback }) => ({
                url: `/version-students/${id}`,
                method: 'PATCH',
                body: { feedback },
            }),
            onQueryStarted: onUpdateTextSubmissionQueryStarted,
        }),
        fetchSimilarAnswers: builder.query({
            query: ({ id, offset }) => ({
                url: `/submissions/similar-submissions/${id}`,
                method: 'GET',
                params: { offset },
            }),
            transformResponse: normalizeFetchSimilarAnswersResponse,
            serializeQueryArgs: ({ endpointName, queryArgs }) => `${endpointName}-${queryArgs.id}`,
            // Always merge incoming data to the cache entry
            merge: (currentCache, responseData) => {
                currentCache.push(...responseData)
            },
            // Refetch when the page or id arg changes
            forceRefetch: ({ currentArg, previousArg }) => (currentArg?.offset ?? 0) !== (previousArg?.offset ?? 0),
        }),
        fetchAnswerCategoryHistory: builder.query({
            query: ({ id }) => ({
                url: `/submission-pattern-history/${id}/by-submission`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchAnswerCategoryHistoryResponse,
        }),
        fetchAnswerCategoryMismatchPredictions: builder.query({
            query: ({ questionId }) => ({
                url: `/questions/${questionId}/label-issues`,
                method: 'POST',
            }),
            transformResponse: normalizeFetchAnswerCategoryMismatchPredictionsResponse,
        }),
        filterByCriteria: builder.query({
            query: ({ token, layerId, criteriaConditions }) => {
                // Create query params with multiple criteria_conditions parameters
                const params = new URLSearchParams()

                // Add each condition as a separate criteria_conditions parameter
                criteriaConditions.forEach(condition => {
                    params.append('criteria_conditions', condition)
                })

                // Add version_id if it exists in the URL
                const versionId = new URLSearchParams(window.location.search).get('version_id')
                if (versionId) {
                    params.append('version_id', versionId)
                }

                return {
                    url: `/assignment-file/${token}/${layerId}/filter-by-criteria`,
                    method: 'GET',
                    params,
                }
            },
            // Add tags for cache invalidation
            providesTags: [CRITERIA_FILTER_TAG],
        }),
        fetchSubmissionPdf: builder.query({
            query: ({ submissionId }) => ({
                url: `/student-attachments/${submissionId}`,
                method: 'GET',
                responseType: 'blob',
            }),
            keepUnusedDataFor: 3600,
        }),
        fetchSubmissionAttachments: builder.query({
            query: ({ submissionId }) => ({
                url: `/student-attachments/${submissionId}/all`,
                method: 'GET',
            }),
            transformResponse: normalizeFetchSubmissionAttachmentsResponse,
            providesTags: (result, error, arg) => [
                { type: ATTACHMENT_TAG, id: arg.submissionId }
            ],
        }),
        downloadAttachment: builder.query({
            query: ({ attachmentId }) => ({
                url: `/student-attachments/download/${attachmentId}`,
                method: 'GET',
                responseType: 'blob',
            }),
            keepUnusedDataFor: 0, // Don't cache blobs
        }),
    }),
})

export const {
    useFetchSimilarSubmissionsBySolutionQuery,
    useFetchSubmissionVersionsQuery,
    useGenerateSimilarSubmissionsMutation,
    useFetchSubmissionsSummaryQuery,
    useLazyDownloadSubmissionsSummaryQuery,
    useFetchSubmissionReviewsQuery,
    useReviewSubmissionMutation,
    useFetchExplanationQuery,
    useFetchNotConfidentSubmissionsQuery,
    useFetchTextAssignmentSubmissionsQuery,
    useFetchQuestionAnswersQuery,
    useFetchTextSubmissionDetailsQuery,
    useReviewQuestionAnswerMutation,
    useApproveAllQuestionAnswersByCategoryMutation,
    useUpdateQuestionAnswerMutation,
    useUpdateQuestionAnswerReasoningMutation,
    useUpdateQuestionAnswerNoteMutation,
    useUpdateQuestionAnswerEscalatedMutation,
    useUpdateTextSubmissionMutation,
    useFetchSimilarAnswersQuery,
    useFetchAnswerCategoryHistoryQuery,
    useFetchAnswerCategoryMismatchPredictionsQuery,
    useFetchEscalatedQuestionAnswersQuery,
    useFilterByCriteriaQuery,
    useFetchSubmissionPdfQuery,
    useLazyFetchSubmissionPdfQuery,
    useFetchSubmissionAttachmentsQuery,
    useLazyDownloadAttachmentQuery,
} = submissionApi
